import { useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Users, X } from 'lucide-react';
import { listOrgUsers } from '@/lib/api';

/**
 * ShareWithPicker — multi-select checkbox list of org staff.
 * Managers use it to share a specific task with extra collaborators
 * beyond the primary `assigned_to` worker.
 *
 * Props:
 *   value: string[]         current collaborator user IDs
 *   onChange: (ids) => void
 *   excludeIds: string[]    hide these ids (e.g. the assigned worker)
 *   testIdPrefix: string
 */
export const ShareWithPicker = ({
  value = [],
  onChange,
  excludeIds = [],
  testIdPrefix = 'share-with',
}) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listOrgUsers();
        if (!cancelled) setUsers(res.data || []);
      } catch (_) {
        // If the user isn't a manager, we simply hide the picker.
        if (!cancelled) setUsers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const excludeSet = new Set(excludeIds.filter(Boolean));
  const staff = users.filter(
    (u) => !excludeSet.has(u.id) && u.role !== 'property_manager' && u.role !== 'owner'
  );

  const toggle = (id) => {
    if (value.includes(id)) {
      onChange(value.filter((v) => v !== id));
    } else {
      onChange([...value, id]);
    }
  };

  if (loading) {
    return (
      <p className="text-xs text-slate-400">Loading team…</p>
    );
  }
  if (staff.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2" data-testid={testIdPrefix}>
      <Label className="flex items-center gap-2">
        <Users className="h-4 w-4" />
        Share with (extra collaborators)
      </Label>
      <p className="text-xs text-slate-500">
        Tick any teammates who should also see full details and be able to update
        this task. The assigned worker is already granted access.
      </p>
      <div className="grid grid-cols-2 gap-2">
        {staff.map((u) => {
          const active = value.includes(u.id);
          return (
            <button
              key={u.id}
              type="button"
              onClick={() => toggle(u.id)}
              className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                active
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
              }`}
              data-testid={`${testIdPrefix}-toggle-${u.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate">
                  {u.name || u.email}
                </span>
                {active && <X className="h-3 w-3 shrink-0" />}
              </div>
              <div className={`text-[10px] uppercase tracking-wide ${active ? 'text-slate-200' : 'text-slate-400'}`}>
                {u.role}
              </div>
            </button>
          );
        })}
      </div>
      {value.length > 0 && (
        <div className="flex gap-1 flex-wrap pt-1">
          {value.map((id) => {
            const u = users.find((x) => x.id === id);
            if (!u) return null;
            return (
              <Badge
                key={id}
                variant="secondary"
                className="text-[10px]"
                data-testid={`${testIdPrefix}-chip-${id}`}
              >
                {u.name || u.email}
              </Badge>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ShareWithPicker;
